import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldAlert, AlertTriangle, CheckCircle2 } from "lucide-react";

function GapRow({ r, onOpenDriver }) {
  return (
    <li className="py-2" data-testid={`missing-row-${r.driver_id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => onOpenDriver?.(r.driver_id)} className="font-semibold text-slate-900 hover:underline" data-testid={`missing-driver-${r.driver_id}`}>{r.driver}</button>
        <span className="font-mono text-[11px] text-slate-400">{r.license}</span>
        {r.critical > 0 && <Badge className="bg-rose-600 text-white border-rose-700">{r.critical} critical</Badge>}
        {r.gaps.length - r.critical > 0 && <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">{r.gaps.length - r.critical} to review</Badge>}
      </div>
      <ul className="mt-1 ml-3 space-y-0.5 text-xs">
        {r.gaps.map((g) => <li key={g.key} className={`flex items-start gap-1.5 ${g.severity === "critical" ? "text-rose-800" : "text-amber-800"}`} data-testid={`missing-gap-${r.driver_id}-${g.key}`}><AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" /><span>{g.label}{g.cite && <span className="text-slate-400"> · 49 CFR {g.cite}</span>}</span></li>)}
      </ul>
    </li>
  );
}

export function MissingComplianceDialog({ open, onClose, customerFilter, isAdmin, onOpenDriver }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/compliance/missing", { params: isAdmin && customerFilter && customerFilter !== "all" ? { customer_id: customerFilter } : {} }).then((r) => setData(r.data)).catch((err) => toast.error(formatApiError(err)));
  }, [open, customerFilter, isAdmin]);
  const groups = data ? Object.entries(data.rows.reduce((acc, r) => { (acc[r.company || "—"] = acc[r.company || "—"] || []).push(r); return acc; }, {})) : [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="missing-compliance-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldAlert className="h-5 w-5 text-rose-700" />Missing compliance · DQ file gaps</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">What each active driver's qualification file is still missing: CDL & med card, the signed annual review <b>with the MVR it was based on</b>, the permanent hire MVR, annual MVR recency, and Clearinghouse items. Fix it from the driver card.</p>
        {data && (
          <div className="flex flex-wrap gap-2 text-xs" data-testid="missing-summary">
            <Badge variant="outline">{data.drivers_checked} drivers checked</Badge>
            <Badge className={data.critical ? "bg-rose-600 text-white border-rose-700" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}>{data.critical} critical</Badge>
            <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">{data.warnings} to review</Badge>
            <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">{data.drivers_checked - data.drivers_with_gaps} complete</Badge>
          </div>
        )}
        {!data && <div className="p-6 text-center text-sm text-slate-500">Checking files…</div>}
        {data && data.rows.length === 0 && <div className="p-8 text-center text-sm text-emerald-700 flex flex-col items-center gap-2" data-testid="missing-none"><CheckCircle2 className="h-8 w-8" />Every active driver's DQ file is complete.</div>}
        {groups.map(([company, rows]) => (
          <div key={company} className="rounded-md border border-slate-200 p-3" data-testid={`missing-company-${company}`}>
            {isAdmin && <div className="tag-mono text-slate-500 mb-1">{company} · {rows.length} driver{rows.length === 1 ? "" : "s"}</div>}
            <ul className="divide-y divide-slate-100">{rows.map((r) => <GapRow key={r.driver_id} r={r} onOpenDriver={onOpenDriver} />)}</ul>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
